/**
 * The post-login shell route — `/` and every rail page path (`/$`, `decisions/0203`). After the
 * auth wall ({@link RequireAuth}) reports `authenticated`, it decides what a visitor sees:
 *
 *  - **GM** → the {@link Shell} with the GM's strip. A GM owns no pilots by design.
 *  - **player with no pilot picked** → the {@link PilotPicker}, full screen: a row of their owned
 *    pilots ending in a ＋ "New pilot" tile. SWITCH in the strip footer returns here.
 *  - **player who has picked a pilot** → the {@link Shell} for that pilot, driven by the
 *    client-side `selectedPilotId` (a *view* concept; never a server active pilot).
 *
 * **Pilotless reach** (`playerLanding`). A link to COMPENDIUM or CODEX reaches the shell even for a
 * player with no pilot picked, and so does a fight on the table for a player who owns no pilot at
 * all: the shell renders with a null pilot, so the page or the stage shows and the pilot-bound
 * pages say there's no pilot.
 *
 * "Owned" is keyed on the logged-in slot id (`pilot.owner === actorId`), read off the shared roster
 * room (`useRemoteRoster`), which stays open under both the picker and the shell. We hold on a
 * "checking" state until the roster welcome seeds authoritative state, so a returning player never
 * sees a wrong surface flash first.
 */
import { useCompendiumContext } from "../../lib/CompendiumProvider";
import { useDelayedFlag } from "../../lib/useDelayedFlag";
import { useGameSessionContext } from "../../lib/game/GameSessionProvider";
import { RequireAuth } from "../../lib/game/RequireAuth";
import { useRemoteRoster } from "../../lib/roster/useRemoteRoster";
import { useRosterArtPrecache } from "../../lib/useRosterArtPrecache";
import { GmsBootHold, GmsBootScreen } from "./GmsBootScreen";
import { ownedPilots, playerLanding } from "./ownedPilot";
import { PilotPicker } from "./PilotPicker";
import { NEW_PILOT_PAGE, requestForgeOpen } from "./forgeGuide";
import { useRail } from "./railState";
import { Shell } from "./shell/Shell";

export function HomePage() {
  return (
    <RequireAuth>
      <HomeRouter />
    </RequireAuth>
  );
}

/** Branch on role: players get the picker or their shell; the GM gets the GM shell. */
function HomeRouter() {
  const session = useGameSessionContext();
  if (session.role !== "player") {
    return <GmDesktop token={session.token!} />;
  }
  return <PlayerLanding actorId={session.actorId!} token={session.token!} />;
}

/**
 * The **GM landing**: open the shared roster room and mount the {@link Shell}. Holds a neutral
 * background until the roster welcome lands (same anti-flash hold as the player path).
 */
function GmDesktop({ token }: { token: string }) {
  const { compendium } = useCompendiumContext();
  const roster = useRemoteRoster(compendium, { token });
  // Tier 1 of the art precache — this table's own chassis first. Called before the loading
  // early-returns below so the hook order stays unconditional.
  useRosterArtPrecache(roster.state);
  const loading = roster.loading || roster.state === null;
  const showChecking = useDelayedFlag(loading, 300);
  if (loading || roster.state === null) {
    if (!showChecking) return <GmsBootHold />;
    return <GmsBootScreen status="LINK" title="Linking to the omninet…" detail="Opening the roster." />;
  }
  return <Shell roster={roster} pilotId={null} />;
}

/**
 * A player: open the roster room, then show the picker until a pilot is picked, at which point the
 * shell renders for that selected pilot. The roster room stays open across both.
 */
function PlayerLanding({ actorId, token }: { actorId: string; token: string }) {
  const session = useGameSessionContext();
  const { compendium } = useCompendiumContext();
  const roster = useRemoteRoster(compendium, { token });
  // Tier 1 of the art precache — this table's own chassis first (see GmDesktop).
  useRosterArtPrecache(roster.state);
  const rail = useRail();
  const compendiumDeepLink = rail.page?.app === "compendium" || rail.page?.app === "codex";

  // The picker's "＋ New pilot" tile: mint a fresh empty pilot + mech on the live roster (a
  // `create_pilot` + `create_mech` pair), select it (so the shell renders for it), and land on
  // PILOT · EDIT with FORGE's panel open (`decisions/0204` point 3). Opening a page here is only
  // legitimate because the click is an explicit user action. The server re-stamps `owner` to the
  // verified slot, so the chosen `actorId` is advisory.
  const onNewPilot = () => {
    const pilotId = crypto.randomUUID();
    const mechId = crypto.randomUUID();
    const loadoutId = crypto.randomUUID();
    const created = roster.dispatch({ type: "create_pilot", actorId, pilotId, callsign: "", name: "", owner: actorId });
    if (!created.ok) return;
    roster.dispatch({ type: "create_mech", actorId, pilotId, mechId, loadoutId, name: "Mech 1" });
    // Land on the page first, then select: selecting mounts the shell, which should open on PILOT ·
    // EDIT rather than flash the bare strip first.
    requestForgeOpen(pilotId);
    void rail.open(NEW_PILOT_PAGE).then(() => session.selectPilot(pilotId));
  };

  // The roster welcome seeds the tile list, so we must wait for it. But that hop is
  // usually a blink on a warm connection — showing a card for it just flashes. Hold a
  // neutral background until the wait crosses ~300ms, then surface the loader (slow link
  // / cold server). The common case goes straight to the picker with no intermediate UI.
  const loading = roster.loading || roster.state === null;
  const showChecking = useDelayedFlag(loading, 300);
  if (loading || roster.state === null) {
    if (!showChecking) return <GmsBootHold />;
    return <GmsBootScreen status="LINK" title="Checking your hangar…" detail="Syncing your roster over the omninet." />;
  }

  const pilots = ownedPilots(roster.state, actorId);
  // The picked pilot must still be one we currently own — a stale localStorage id (a pilot
  // since deleted, or a slot that no longer owns it) falls back to the picker.
  const selected = pilots.find((p) => p.id === session.selectedPilotId) ?? null;

  const landing = playerLanding({ ownedCount: pilots.length, picked: selected !== null, deepLink: compendiumDeepLink, scene: session.tableScene });
  if (selected === null) {
    // A COMPENDIUM/CODEX deep-link, or a fight on the table for a player with no pilot, reaches
    // the shell pilotless. Otherwise, the normal picker.
    if (landing === "shell-pilotless") return <Shell roster={roster} pilotId={null} />;
    return <PilotPicker pilots={pilots} onSelect={session.selectPilot} onNewPilot={onNewPilot} />;
  }
  // Hand the shell the SHARED roster room (not a snapshot): every page reads and writes the same
  // authoritative state on the same socket, so live grit/stats and per-actor undo come from one
  // source, never re-derived.
  return <Shell roster={roster} pilotId={selected.id} />;
}
